import React, { useState } from 'react'
import { router, usePage, Link, Head } from '@inertiajs/react'
import { Lock, Loader2, Check, ShieldCheck, AlertCircle, Eye, EyeOff } from 'lucide-react'
import ClaimShell from '@/components/claim/ClaimShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { csrfToken } from '@/lib/csrf'

interface SetPasswordProps {
  email: string
  invitationToken: string
}

// Mirror of User::PASSWORD_MIN + the per-model complexity rule (app/models/user.rb).
// These are DISPLAY-ONLY hints so the customer sees progress; the backend Devise
// `:validatable` + `password_strength` validation remains the single source of truth.
const PASSWORD_MIN = 12

function passwordChecks(pw: string) {
  return [
    { label: `At least ${PASSWORD_MIN} characters`, ok: pw.length >= PASSWORD_MIN },
    { label: 'An uppercase letter', ok: /[A-Z]/.test(pw) },
    { label: 'A lowercase letter', ok: /[a-z]/.test(pw) },
    { label: 'A number', ok: /\d/.test(pw) },
  ]
}


export default function SetPassword({ email, invitationToken }: SetPasswordProps) {
  // The claim controller inherits ApplicationController (not Operator::BaseController), so a
  // redirect's flash surfaces on Inertia's shared `flash` prop — read here, display only.
  const { props } = usePage<{ flash?: { alert?: string | null; notice?: string | null } }>()
  const flashAlert = props.flash?.alert

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [tos, setTos] = useState(false)
  const [privacy, setPrivacy] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const checks = passwordChecks(password)
  const passwordValid = checks.every((c) => c.ok)
  const confirmMatches = confirm.length > 0 && confirm === password
  const canSubmit = passwordValid && confirmMatches && tos && privacy && !submitting

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    // Destructive/state-changing — router.patch (the documented exception to the <Link>
    // nav rule). The CSRF token is also forwarded as a header for parity with the
    // onboarding fetch idiom; Inertia includes the form-encoded body automatically.
    router.patch(
      '/claim',
      { invitation_token: invitationToken, password, tos_accepted: tos, privacy_accepted: privacy },
      {
        headers: { 'X-CSRF-Token': csrfToken() },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <ClaimShell currentStep={1}>
      <Head title="Set your password" />
      <div className="mx-auto max-w-xl space-y-6">
        {flashAlert && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 animate-fade-in-up">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{flashAlert}</span>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
          {/* Card header */}
          <div className="flex items-center gap-3 border-b border-border/40 px-6 py-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Set your password</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                You&apos;re claiming the account for{' '}
                <span className="font-medium text-foreground">{email}</span>
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 p-6">
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Create a strong password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={show ? 'Hide password' : 'Show password'}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Live strength checklist — display only */}
              <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {checks.map((c) => (
                  <li
                    key={c.label}
                    className={cn(
                      'flex items-center gap-1.5 text-xs transition-colors',
                      c.ok ? 'text-emerald-600' : 'text-muted-foreground'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-full transition-colors',
                        c.ok ? 'bg-emerald-100 text-emerald-600' : 'bg-muted text-muted-foreground/50'
                      )}
                    >
                      <Check className="h-2.5 w-2.5" />
                    </span>
                    {c.label}
                  </li>
                ))}
              </ul>
            </div>

            {/* Confirm */}
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="Re-enter your password"
              />
              {confirm.length > 0 && !confirmMatches && (
                <p className="text-xs text-rose-600">Passwords don&apos;t match.</p>
              )}
            </div>

            {/* Consent checkboxes */}
            <div className="space-y-3 rounded-lg border border-border/60 bg-muted/40 p-4">
              <ConsentRow checked={tos} onChange={setTos}>
                I agree to the{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary hover:underline"
                >
                  Terms of Service
                </a>
              </ConsentRow>
              <ConsentRow checked={privacy} onChange={setPrivacy}>
                I have read the{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary hover:underline"
                >
                  Privacy Policy
                </a>
              </ConsentRow>
            </div>

            <Button type="submit" disabled={!canSubmit} className="w-full rounded-lg">
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Claiming your account…
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Set password &amp; continue
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Already claimed?{' '}
          <Link href="/users/sign_in" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </ClaimShell>
  )
}

// A single accessible consent row — a styled checkbox driving a controlled boolean.
function ConsentRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  children: React.ReactNode
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm text-foreground">
      <span className="relative mt-0.5 flex h-4 w-4 shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-border bg-white transition-colors checked:border-primary checked:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        />
        <Check className="pointer-events-none absolute left-0 top-0 h-4 w-4 scale-0 text-white transition-transform peer-checked:scale-100" />
      </span>
      <span className="leading-snug">{children}</span>
    </label>
  )
}
